/**
 * This layout is complex, and can probably be simplified.
 * When modifying these screens, there are many edge cases
 * to consider and test:
 *
 * - Pinned and unpinned side-nav
 * - Courses with and without banner images
 * - Screens of various sizes
 * - All of the student-facing course screens
 *
 * That list seems small, but the permutations and combinations
 * are tricky, and often lead to weird edge cases and incorrect
 * spacing, so test thoroughly and proceed with caution!
 */

import { StudentThemeProvider } from '@course/components/student-theme-picker';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { AipCourseNav } from './aip-course-nav';
import { BtnManageCourse } from './btn-manage-course';
import { CourseNavLinks } from './course-nav-links';
import { PageHeader } from './page-header';
import { Props, StudentTabs } from './props';
import { SideNavProvider, StudentSideNav } from './student-side-nav';
import { intl } from 'shared/intl/use-intl';

const PAGE_TITLES: Record<StudentTabs, string | undefined> = {
  home: intl('Overview'),
  calendar: intl('Calendar'),
  discussions: intl('Discussions'),
  meetings: intl('Meetings'),
  preferences: intl('Course Preferences'),
  checkins: intl('Standups'),
  tracker: intl('Tracker'),
  modules: undefined,
  people: undefined,
};

export function StudentPage({
  children,
  course,
  currentLink,
  sideNavContent,
  sidePaneContent,
  accessLevel = 'student',
  isPinnable = false,
  hiddenSideNavContent,
  editLink = {},
  p = 'py-12 xl:py-20',
  documentTitle,
}: Props) {
  useDocumentTitle([documentTitle, PAGE_TITLES[currentLink], course.title]);

  const isAip = course.id === AIP_COURSE_ID;
  const contentPadding = isAip ? 'py-6 xl:py-8' : p;

  return (
    <StudentThemeProvider forceLight={isAip}>
      <SideNavProvider isPinnable={isPinnable} storageKey="studentPinSideNav">
        <div class={`flex ${isAip ? 'lg:bg-gray-100 dark:lg:bg-gray-950' : ''}`}>
          <nav class="hidden lg:flex lg:sticky top-0 h-screen relative z-40">
            {isAip ? (
              <AipCourseNav currentLink={currentLink} />
            ) : (
              <CourseNavLinks
                isThemable
                course={course}
                currentLink={currentLink}
                accessLevel={accessLevel}
              />
            )}
          </nav>
          <div
            class={`relative flex grow flex-col min-h-screen student-page ${
              isAip
                ? 'bg-white dark:bg-gray-900 lg:m-2 lg:ml-0 lg:rounded-xl lg:shadow-sm'
                : 'bg-white dark:bg-gray-900'
            }`}
          >
            <PageHeader
              course={course}
              accessLevel={accessLevel}
              isAip={isAip}
              manageButton={
                editLink && (
                  <BtnManageCourse
                    displayClass="hidden md:inline-flex"
                    course={course}
                    url={editLink.url}
                  />
                )
              }
            />
            <BtnManageCourse
              displayClass="inline-flex md:hidden"
              class="mx-4 mt-8"
              course={course}
              url={editLink.url}
            />
            <div class="relative flex grow">
              <div class={sideNavContent ? '' : 'lg:hidden'}>
                <StudentSideNav
                  course={course}
                  currentLink={currentLink}
                  hiddenSideNavContent={hiddenSideNavContent}
                  accessLevel={accessLevel}
                  isAip={isAip}
                >
                  {sideNavContent}
                </StudentSideNav>
              </div>
              <div class="flex grow flex-col lg:flex-row">
                <div class={`relative flex grow max-w-full dark:bg-transparent ${contentPadding}`}>
                  {children}
                </div>
                {sidePaneContent}
              </div>
            </div>
          </div>
        </div>
      </SideNavProvider>
    </StudentThemeProvider>
  );
}
